import classNames from 'classnames';
import useStyles from 'isomorphic-style-loader/useStyles';
import type {
  CSSProperties,
  HTMLAttributes,
  PropsWithChildren,
  ReactElement,
} from 'react';
import type { SortingRule } from 'react-table';
import { Icon } from 'semantic-ui-react';

import ColumnDescription from '@components/table/column-description';
import { swallowNextClick } from '@components/table/use-column-resize';

import type { ReactTableHeaderGroup } from './types';
import s from './react-table.scss';

/**
 * Arms the guard that swallows the click a released resize drag fires — from the
 * gesture itself, rather than the resize reducer React may invoke twice per action.
 */
const armResizeClickGuard = () =>
  document.addEventListener('mouseup', swallowNextClick, { once: true });

interface HeaderResizeColumn {
  _index: number;
  fixedWidth?: number;
  id?: string;
  resizable?: boolean;
}

export interface TableHeadProps<T extends object> {
  /**
   * Wrapper element the header rows render into (the `<thead>` equivalent).
   */
  Wrapper: (
    props: PropsWithChildren<
      HTMLAttributes<HTMLElement> & { minWidth?: string }
    >,
  ) => ReactElement;
  /**
   * All columns, used to resolve the resize order via each column's `_index`.
   */
  columns: HeaderResizeColumn[];
  /**
   * Resolves the fixed-width style for a column header.
   */
  getColumnStyle: (column: ReactTableHeaderGroup<T>) => CSSProperties;
  /**
   * The table's header groups.
   */
  headerGroups: ReactTableHeaderGroup<T>[];
  /**
   * Whether the header is currently pinned to the sticky boundary.
   */
  isPinned: boolean;
  /**
   * Minimum header width, e.g. to constrain the table for printing.
   */
  minWidth?: string;
  /**
   * Whether columns are resizable.
   */
  resizable: boolean;
  /**
   * Commits a new sort order.
   */
  setSortBy: (sortBy: SortingRule<T>[]) => void;
  /**
   * The current sort order.
   */
  sortBy: SortingRule<T>[];
  /**
   * Additional props forwarded to the wrapper element.
   */
  tableHeadProps: HTMLAttributes<HTMLTableSectionElement>;
  /**
   * Visible columns, used to find the resizable columns to a column's right.
   */
  visibleColumns: HeaderResizeColumn[];
}

/**
 * Renders the table header: sort icons and `aria-sort`, resize handles,
 * description popups, and keyboard (Enter/Space) sort toggling.
 *
 * @deprecated Internal to the legacy `ReactTable`; superseded by the table
 * primitives in `@components/table`.
 */
const TableHead = <T extends object>({
  Wrapper,
  columns,
  getColumnStyle,
  headerGroups,
  isPinned,
  minWidth,
  resizable,
  setSortBy,
  sortBy,
  tableHeadProps,
  visibleColumns,
}: TableHeadProps<T>) => {
  useStyles(s);

  return (
    <Wrapper
      {...tableHeadProps}
      minWidth={minWidth}
      className={classNames(
        tableHeadProps.className,
        s.header,
        isPinned && s.pinned,
      )}
    >
      {
        // Looping over the header rows
        headerGroups.map(headerGroup => {
          const { key: headerGroupKey, ...headerGroupProps } =
            headerGroup.getHeaderGroupProps();
          return (
            // Applying the header row props
            <div key={headerGroupKey} {...headerGroupProps} className="rt-tr">
              {
                // Looping over the headers in each row
                headerGroup.headers.map(column => {
                  const isResizable =
                    resizable &&
                    (column.resizable ?? true) &&
                    !column.fixedWidth;
                  const sortable = column.sortable ?? column.canSort;
                  const originalColumn = columns.find(
                    candidate => candidate.id === column.id,
                  );
                  if (!originalColumn)
                    throw new Error(`Column ${column.id} not found`);
                  const columnsToRight = visibleColumns.filter(
                    candidate =>
                      (candidate.resizable ?? true) &&
                      !candidate.fixedWidth &&
                      // eslint-disable-next-line no-underscore-dangle
                      candidate._index > originalColumn._index,
                  );
                  const lastResizableColumn = columnsToRight.length === 0;

                  const { isSorted, isSortedDesc } =
                    column.config?.getSorted?.(column, sortBy) ?? column;

                  const { key: headerKey, ...headerProps } =
                    column.getHeaderProps([
                      {
                        className: classNames(
                          'rt-th',
                          s.columnHeader,
                          sortable && s.sortable,
                          column.headerClassName,
                          resizable ? 'rt-resizable-header' : '',
                        ),
                        style: {
                          ...getColumnStyle(column),
                          ...column.headerStyle,
                          ...(sortable
                            ? column.getSortByToggleProps().style
                            : {}),
                        },
                      },
                    ]);
                  const handleSort = sortable
                    ? (event: React.SyntheticEvent) => {
                        // If column has a custom sort function, use that instead of calling the onClick function
                        const updatedSortBy = column.config?.customSortFn?.(
                          column,
                          sortBy,
                        );

                        if (updatedSortBy) {
                          setSortBy(updatedSortBy);
                          return;
                        }

                        // Handle firstClickDescending option
                        if (column.config?.firstClickDescending) {
                          const currentSort = sortBy.find(
                            sort => sort.id === column.id,
                          );

                          setSortBy([
                            {
                              id: column.id,
                              desc: currentSort ? !currentSort.desc : true,
                            },
                          ]);
                          return;
                        }

                        // Toggle only reads shiftKey; KeyboardEvent has it.
                        column
                          .getSortByToggleProps()
                          .onClick?.(event as React.MouseEvent);
                      }
                    : undefined;

                  const isInteractive = sortable || !!column.description;

                  const headerContent = (
                    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
                    <div
                      aria-sort={
                        isSorted
                          ? isSortedDesc
                            ? 'descending'
                            : 'ascending'
                          : 'none'
                      }
                      role={sortable ? 'button' : undefined}
                      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- description-only headers are intentionally focusable for keyboard tooltip access
                      tabIndex={isInteractive ? 0 : undefined}
                      {...headerProps}
                      onClick={handleSort}
                      onKeyDown={
                        sortable
                          ? (event: React.KeyboardEvent) => {
                              if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                handleSort?.(event);
                              }
                            }
                          : undefined
                      }
                    >
                      {sortable &&
                        (column.config?.useSingleSortIcon ? (
                          <Icon
                            name="circle"
                            color={isSorted ? 'teal' : 'grey'}
                            className={classNames(s.sortIcon, s.singleSortIcon)}
                          />
                        ) : (
                          <Icon.Group className={s.sortIcon} size="large">
                            <Icon
                              name="sort up"
                              color={
                                isSorted && !isSortedDesc ? 'teal' : 'grey'
                              }
                            />
                            <Icon
                              name="sort down"
                              color={isSortedDesc ? 'teal' : 'grey'}
                            />
                          </Icon.Group>
                        ))}
                      <div
                        className={classNames(
                          s.columnHeaderContent,
                          isResizable
                            ? 'rt-resizable-header-content'
                            : 'rt-header-content',
                        )}
                      >
                        {column.render('Header')}
                      </div>
                      {isResizable && !lastResizableColumn ? (
                        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
                        <div
                          {...column.getResizerProps()}
                          onClick={event => event.stopPropagation()}
                          onPointerDown={armResizeClickGuard}
                          className="rt-resizer"
                        />
                      ) : null}
                    </div>
                  );

                  return (
                    <ColumnDescription
                      key={headerKey}
                      description={column.description}
                      trigger={headerContent}
                    />
                  );
                })
              }
            </div>
          );
        })
      }
    </Wrapper>
  );
};

export default TableHead;
